<template>
  <div class="flex max-w-full flex-col gap-2 text-left" :class="containerClass">
    <div
      v-if="isImage"
      class="overflow-hidden rounded-[12px]"
      :class="imageFrameClass"
    >
      <button
        v-if="previewUrl"
        type="button"
        class="block"
        @click="openAttachment"
      >
        <img
          :src="previewUrl"
          :alt="displayName"
          class="block max-h-[260px] max-w-full object-cover cursor-zoom-in"
          @error="handleImageError"
        />
      </button>
      <div
        v-else
        class="flex min-h-[120px] flex-col items-center justify-center gap-3 px-4 py-6 text-center text-[14px]"
        :class="placeholderTextClass"
      >
        <span
          v-if="showPlaceholderLoader"
          class="relative block h-9 w-9"
          aria-hidden="true"
        >
          <span
            class="absolute inset-0 rounded-full border-2 border-current/20"
          />
          <span
            class="absolute inset-0 rounded-full border-2 border-transparent border-t-current animate-spin"
          />
        </span>
        {{ imagePlaceholderLabel }}
      </div>
    </div>

    <div
      class="group relative flex max-w-full items-center gap-2 rounded-[10px] border px-3 py-2"
      :class="[metaClass, metaInteractiveClass]"
      :role="isMetaInteractive ? 'button' : undefined"
      :tabindex="isMetaInteractive ? 0 : undefined"
      :aria-label="isMetaInteractive ? 'Скачать файл' : undefined"
      :aria-disabled="!isMetaInteractive"
      @click="handleMetaClick"
      @keydown.enter.prevent="handleMetaClick"
      @keydown.space.prevent="handleMetaClick"
    >
      <SvgoIconComponentChatIconFile class="shrink-0"></SvgoIconComponentChatIconFile>
      <span class="min-w-0 flex-1 truncate">
        {{ displayName }}
      </span>
      <div
        v-if="showMetaSpinner"
        class="shrink-0 flex items-center gap-2 text-[13px] font-medium"
        :class="statusTextClass"
      >
        <span class="relative block h-4 w-4" aria-hidden="true">
          <span
            class="absolute inset-0 rounded-full border-2 border-current/20"
          />
          <span
            class="absolute inset-0 rounded-full border-2 border-transparent border-t-current animate-spin"
          />
        </span>
        <span>{{ metaStatusLabel }}</span>
      </div>
      <span
        v-else
        class="shadow-tooltip-dark pointer-events-none absolute -top-10 right-2 inline-flex items-center rounded-full px-3 py-1 text-[12px] font-medium opacity-0 translate-y-1 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:opacity-100 group-focus-visible:-translate-y-0.5 group-focus-visible:opacity-100"
        :class="downloadHintClass"
      >
        Скачать
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useHelpChatApi } from "~/composables/useHelpChatApi";
import type { HelpChatFile, HelpChatFileUploadStatus } from "~/types/helpChat";

const IMAGE_EXTENSIONS = new Set([
  "avif",
  "bmp",
  "gif",
  "heic",
  "heif",
  "jpeg",
  "jpg",
  "png",
  "svg",
  "webp",
]);
const UUID_PREFIX_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_(.+)$/i;
const URL_REFRESH_BUFFER_MS = 15_000;
const DEFAULT_URL_TTL_MS = 5 * 60_000;
const FILE_STATUS_POLL_INTERVAL_MS = 2_000;
const FILE_STATUS_RETRY_INTERVAL_MS = 4_000;

const props = withDefaults(
  defineProps<{
    file: HelpChatFile;
    variant?: "system" | "incoming" | "outgoing";
  }>(),
  {
    variant: "incoming",
  },
);

const { getFileDownloadLink, getFileUploadStatus } = useHelpChatApi();

const loading = ref(false);
const previewUrl = ref("");
const previewExpiresAt = ref(0);
const hasRetriedImageLoad = ref(false);
const uploadStatus = ref<HelpChatFileUploadStatus["status"] | "checking">(
  "checking",
);
let uploadStatusTimer: ReturnType<typeof setTimeout> | null = null;
let uploadStatusPromise: Promise<boolean> | null = null;
let uploadStatusRevision = 0;
let isDisposed = false;

const rawFileName = computed(() => {
  const directName = String(props.file?.name ?? "").trim();
  if (directName) return decodeURIComponent(directName);

  const path = String(props.file?.path ?? "").trim();
  if (!path) return "";

  const parts = path.split("/");
  return decodeURIComponent(parts[parts.length - 1] || "");
});

const displayName = computed(() => {
  const name = rawFileName.value;
  if (!name) return "Файл";

  const withOriginalName = name.match(UUID_PREFIX_REGEX);
  if (withOriginalName?.[1]) {
    return withOriginalName[1];
  }

  return name;
});

const extension = computed(() => {
  const fileName = displayName.value;
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) return "";
  return fileName.slice(dotIndex + 1).toLowerCase();
});

const isImage = computed(() => IMAGE_EXTENSIONS.has(extension.value));

const containerClass = computed(() => {
  return props.variant === "system" ? "items-center" : "items-start";
});

const metaClass = computed(() => {
  if (props.variant === "outgoing") {
    return "border-white/25 bg-white/12 text-white hover:bg-white/18";
  }

  return "border-gray-border bg-white text-gray-dark hover:border-orange hover:text-orange";
});

const metaTextClass = computed(() => {
  return props.variant === "outgoing" ? "text-white" : "text-extra-text-color";
});

const imageFrameClass = computed(() => {
  return props.variant === "outgoing" ? "bg-white/12" : "bg-white";
});

const statusTextClass = computed(() => {
  return props.variant === "outgoing" ? "text-white" : "text-orange";
});

const isAttachmentUploaded = computed(() => uploadStatus.value === "uploaded");
const showUploadSpinner = computed(() => uploadStatus.value !== "uploaded");
const showMetaSpinner = computed(
  () => showUploadSpinner.value || loading.value,
);
const isMetaInteractive = computed(() => !showMetaSpinner.value);
const showPlaceholderLoader = computed(() => {
  return (
    uploadStatus.value === "checking" ||
    showUploadSpinner.value ||
    loading.value
  );
});
const metaInteractiveClass = computed(() => {
  return isMetaInteractive.value
    ? "shadow-interactive-soft cursor-pointer transition-[transform,box-shadow,border-color,color] duration-200 hover:-translate-y-px"
    : "cursor-default";
});
const downloadHintClass = computed(() => {
  return props.variant === "outgoing"
    ? "border border-white/50 bg-white text-gray-dark"
    : "bg-gray-dark text-white";
});

const placeholderTextClass = computed(() => {
  return showUploadSpinner.value ? statusTextClass.value : metaTextClass.value;
});

const imagePlaceholderLabel = computed(() => {
  if (uploadStatus.value === "checking") return "Проверяем изображение...";
  if (showUploadSpinner.value) return "Файл загружается...";
  if (loading.value) return "Загружаем изображение...";
  return "Изображение недоступно";
});

const uploadStatusLabel = computed(() => {
  return uploadStatus.value === "checking" ? "Проверяем..." : "Загружаем...";
});
const metaStatusLabel = computed(() => {
  if (uploadStatus.value === "checking") return "Проверяем...";
  if (showUploadSpinner.value) return "Загружаем...";
  if (loading.value) return "Подготавливаем...";
  return "";
});

const stopUploadStatusPolling = () => {
  if (!uploadStatusTimer) return;
  clearTimeout(uploadStatusTimer);
  uploadStatusTimer = null;
};

const scheduleUploadStatusPolling = (
  delayMs = FILE_STATUS_POLL_INTERVAL_MS,
) => {
  if (!process.client || isDisposed) return;

  stopUploadStatusPolling();
  const currentRevision = uploadStatusRevision;
  uploadStatusTimer = setTimeout(() => {
    if (isDisposed || currentRevision !== uploadStatusRevision) return;
    void refreshUploadStatus();
  }, delayMs);
};

const refreshUploadStatus = async () => {
  if (!process.client || isDisposed) return isAttachmentUploaded.value;
  if (uploadStatusPromise) return uploadStatusPromise;

  stopUploadStatusPolling();
  const currentRevision = uploadStatusRevision;
  uploadStatusPromise = (async () => {
    try {
      const response = await getFileUploadStatus(Number(props.file.id));
      if (isDisposed || currentRevision !== uploadStatusRevision) return false;

      const nextStatus =
        response?.uploaded || response?.status === "uploaded"
          ? "uploaded"
          : "not_uploaded";

      uploadStatus.value = nextStatus;
      if (nextStatus !== "uploaded") {
        scheduleUploadStatusPolling();
      }

      return nextStatus === "uploaded";
    } catch (error) {
      if (isDisposed || currentRevision !== uploadStatusRevision) return false;
      console.error("Failed to check chat attachment upload status", error);
      uploadStatus.value = "not_uploaded";
      scheduleUploadStatusPolling(FILE_STATUS_RETRY_INTERVAL_MS);
      return false;
    } finally {
      uploadStatusPromise = null;
    }
  })();

  return uploadStatusPromise;
};

const ensureAttachmentUploaded = async () => {
  if (isAttachmentUploaded.value) return true;
  return refreshUploadStatus();
};

const hasFreshPreviewUrl = () => {
  return (
    Boolean(previewUrl.value) &&
    previewExpiresAt.value - Date.now() > URL_REFRESH_BUFFER_MS
  );
};

const resolveExpiresAt = (value?: string) => {
  const parsed = Date.parse(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : Date.now() + DEFAULT_URL_TTL_MS;
};

const ensurePreviewUrl = async (force = false) => {
  if (!isImage.value) return "";
  if (!(await ensureAttachmentUploaded())) return "";
  if (!force && hasFreshPreviewUrl()) return previewUrl.value;
  if (loading.value) return previewUrl.value;

  loading.value = true;
  try {
    const response = await getFileDownloadLink(Number(props.file.id));
    const url = String(response?.url ?? "").trim();
    if (!url) {
      throw new Error("Ссылка на изображение не получена");
    }

    previewUrl.value = url;
    previewExpiresAt.value = resolveExpiresAt(response?.expires_at);
    return url;
  } finally {
    loading.value = false;
  }
};

const openAttachment = async () => {
  if (!process.client || loading.value) return;
  if (!(await ensureAttachmentUploaded())) return;

  const openedWindow = window.open("", "_blank", "noopener,noreferrer");

  try {
    let url = "";

    if (isImage.value) {
      url = (await ensurePreviewUrl()) || previewUrl.value;
    } else {
      loading.value = true;
      try {
        const response = await getFileDownloadLink(Number(props.file.id));
        url = String(response?.url ?? "").trim();
      } finally {
        loading.value = false;
      }
    }

    if (!url) {
      throw new Error("Ссылка на файл не получена");
    }

    if (openedWindow) {
      openedWindow.location.replace(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    openedWindow?.close();
    console.error("Failed to open chat attachment", error);
  }
};

const triggerBrowserDownload = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = displayName.value;
  document.body.append(link);
  link.click();
  link.remove();
};

const downloadAttachment = async () => {
  if (!process.client || loading.value) return;
  if (!(await ensureAttachmentUploaded())) return;

  try {
    let url = "";

    if (isImage.value) {
      url = (await ensurePreviewUrl()) || previewUrl.value;
    } else {
      loading.value = true;
      try {
        const response = await getFileDownloadLink(Number(props.file.id));
        url = String(response?.url ?? "").trim();
      } finally {
        loading.value = false;
      }
    }

    if (!url) {
      throw new Error("Ссылка на файл не получена");
    }

    triggerBrowserDownload(url);
  } catch (error) {
    console.error("Failed to download chat attachment", error);
  }
};

const handleMetaClick = () => {
  if (!isMetaInteractive.value) return;
  void downloadAttachment();
};

const handleImageError = async () => {
  if (hasRetriedImageLoad.value) return;
  hasRetriedImageLoad.value = true;
  previewUrl.value = "";

  try {
    await ensurePreviewUrl(true);
  } catch (error) {
    console.error("Failed to reload chat image preview", error);
  }
};

watch(
  () => [props.file.id, props.file.path, props.file.name],
  () => {
    uploadStatusRevision += 1;
    uploadStatusPromise = null;
    stopUploadStatusPolling();
    previewUrl.value = "";
    previewExpiresAt.value = 0;
    hasRetriedImageLoad.value = false;
    uploadStatus.value = "checking";
    void refreshUploadStatus();
  },
  { immediate: true },
);

watch(isAttachmentUploaded, (uploaded) => {
  if (!uploaded || !isImage.value) return;
  void ensurePreviewUrl();
});

onBeforeUnmount(() => {
  isDisposed = true;
  stopUploadStatusPolling();
});
</script>
